/**
 * @architecture-header 1
 * @layer module
 * @domain activities
 * @responsibility Render the paper editor with its review navigator and scan pane.
 * @state-owner activities UI store owns zoom, reference pane and question selection
 * @allowed-imports React; same-module store, actions, queries, mutations and dialogs; workspace store; shared components; active context
 * @forbidden direct API client; student-only surfaces
 * @public-api workspace main column
 * @side-effects dataset updates and toasts
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import { useActiveContextId } from '../../../app/providers/ActiveRoleProvider';
import { useOverlay } from '../../../shared/components/OverlayHost';
import { PanelResizer } from '../../../shared/components/Resizer';
import {
  EyeIcon,
  RedoIcon,
  StepNextIcon,
  StepPrevIcon,
  UndoIcon,
} from '../../../shared/components/VispoIcon';
import { QUESTIONS } from '../../../shared/lib/demo-data';
import { clickable } from '../../../shared/lib/interactive';
import type { ActivityRecord } from '../../../shared/lib/model';
import { useTranslator } from '../../../shared/model/use-workspace';
import { useWorkspaceUiStore } from '../../../shared/model/workspace-store';
import { useUpdateActivity } from '../api/activities.mutations';
import { useActivityEditorItems } from '../api/activities.queries';
import type { ActivityEditorItem, ActivityQuestion, StudentActivityChoice } from '../model/types';
import { useActivityActions } from '../model/use-activity-actions';
import { useActivityUiStore } from '../store/activity-ui-store';
import { CopyActivityItemDialog } from './CopyActivityItemDialog';

const OPTION_LABELS = ['(A)', '(B)', '(C)', '(D)'];
const OPTION_VALUES = ['12', '16', '9', '20'];

export function ActivityEditor({ activity }: { activity: ActivityRecord }) {
  const t = useTranslator();
  const { openModal } = useOverlay();
  const notify = useWorkspaceUiStore((state) => state.notify);
  const actions = useActivityActions();
  const updateActivity = useUpdateActivity();
  const contextId = useActiveContextId();
  const selectedQuestion = useActivityUiStore((state) => state.selectedQuestion);
  const setSelectedQuestion = useActivityUiStore((state) => state.setSelectedQuestion);
  const zoom = useActivityUiStore((state) => state.zoom);
  const changeZoom = useActivityUiStore((state) => state.changeZoom);
  const setZoom = useActivityUiStore((state) => state.setZoom);
  const referenceOpen = useActivityUiStore((state) => state.referenceOpen);
  const setReferenceOpen = useActivityUiStore((state) => state.setReferenceOpen);
  const editorItems = useActivityEditorItems(activity.id);
  const liveItems = editorItems.data?.items ?? [];
  const questions = liveItems.length
    ? liveItems.map(activityQuestionFromEditorItem)
    : QUESTIONS.map(
        (question, index): ActivityQuestion => ({
          id: `q${index}`,
          prompt: question.text,
          answer: question.answer,
          concept: question.concept,
          reviewed: Boolean(activity.reviewed[index]),
        }),
      );
  const reviewed = liveItems.length
    ? liveItems.map((item) => item.qaApprovedRevision === item.revision)
    : activity.reviewed;

  if (activity.status === 'failed') {
    return (
      <div className="wrap" style={{ maxWidth: 720 }}>
        <div className="hd">
          <div>
            <h1>{activity.name}</h1>
            <p className="sub">{t('解析失敗')}</p>
          </div>
        </div>
        <section className="panel">
          <header>
            <h2>{t('這份沒有解析成功')}</h2>
          </header>
          <div className="eqbox" style={{ fontSize: 16, lineHeight: '26px' }}>
            {t('第 3 頁的版面辨識不出來，可能是掃描歪斜或解析度太低。')}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="bt pri" type="button" onClick={() => notify(t('已重新排入解析'))}>
              {t('重新解析')}
            </button>
            <button className="bt" type="button" onClick={() => notify(t('已改用原圖，不切題'))}>
              {t('改用原圖，不切題')}
            </button>
          </div>
        </section>
      </div>
    );
  }

  const pending = reviewed.filter((value) => !value).length;

  const stepReview = (direction: number) => {
    const total = questions.length;
    for (let step = 1; step <= total; step += 1) {
      const index = (((selectedQuestion + direction * step) % total) + total) % total;
      if (!reviewed[index]) {
        setSelectedQuestion(index);
        window.setTimeout(() => {
          document
            .getElementById(`q${index}`)
            ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 30);
        return;
      }
    }
    notify(t('沒有待核准的題目'));
  };

  return (
    <div className="xedwrap">
      <div className="xedbar">
        <input
          className="xedt"
          defaultValue={activity.name}
          key={activity.id}
          onBlur={(event) => actions.rename(activity.id, event.target.value)}
        />
        <div className="sp" />
        <button className="ib2" title={t('復原')} type="button" onClick={() => notify(t('已復原'))}>
          <UndoIcon />
        </button>
        <button className="ib2" title={t('重做')} type="button" onClick={() => notify(t('已重做'))}>
          <RedoIcon />
        </button>
        {activity.answerSource === 'ai' ? (
          <span className="xpill warn" title={t('沒有附答案檔')}>
            {t('AI 答案')}
          </span>
        ) : null}
        {pending ? (
          <span className="vrn">
            <button
              className="vrnb"
              title={`${t('上一個待核准')} · P`}
              type="button"
              onClick={() => stepReview(-1)}
            >
              <StepPrevIcon />
            </button>
            <span className="vrnl">{`${pending} ${t('題待核准')}`}</span>
            <button
              className="vrnb"
              title={`${t('下一個待核准')} · N`}
              type="button"
              onClick={() => stepReview(1)}
            >
              <StepNextIcon />
            </button>
          </span>
        ) : (
          <span className="xpill ok">{t('全部已核准')}</span>
        )}
        <button
          className={`bt ${referenceOpen ? 'on' : ''}`}
          type="button"
          onClick={() => setReferenceOpen(!referenceOpen)}
        >
          {t('參考原卷')}
        </button>
        <button
          className="bt pri"
          disabled={pending > 0}
          type="button"
          onClick={() => {
            updateActivity.mutate({
              activityId: activity.id,
              contextId,
              questions,
              title: activity.name,
            });
            actions.save(activity.id);
          }}
        >
          {t('更新')}
        </button>
      </div>

      <div className={`xedmain ${referenceOpen ? 'withref' : ''}`}>
        <div className="xedpaper">
          <div className="xptool">
            <EyeIcon />
            <b>{t('題目')}</b>
            <div className="sp" />
            <button className="xzb" title={t('縮小')} type="button" onClick={() => changeZoom(-10)}>
              −
            </button>
            <button
              className="xzv"
              title={t('回到 100%')}
              type="button"
              onClick={() => setZoom(100)}
            >
              {`${zoom}%`}
            </button>
            <button className="xzb" title={t('放大')} type="button" onClick={() => changeZoom(10)}>
              +
            </button>
          </div>
          <div className="xsheetwrap">
            <div className="xsheet" style={{ ['--zm' as string]: String(zoom / 100) }}>
              <div className="xshead">
                <div className="xht">
                  <b>{activity.name}</b>
                  <span>{t('第 1 頁 / 共 1 頁')}</span>
                </div>
                <div className="sp" />
                <span className="xcode">{activity.id.toUpperCase()}</span>
              </div>
              <div className="xsmeta">
                <span>{`${t('題數')}: ${questions.length}`}</span>
                <em>|</em>
                <span>{`${t('總分')}: 100`}</span>
              </div>
              <div className="xsfld">
                <span>
                  {t('班級')}
                  <i />
                </span>
                <span>
                  {t('姓名')}
                  <i />
                </span>
                <span>
                  {t('日期')}
                  <i />
                </span>
              </div>
              <div className="xsbody">
                <h2 className="xstitle">{activity.name}</h2>
                <div className="xsnote">{t('點一下新增說明')}</div>
                <div className="xsgrp">{t('題組')}</div>
                {questions.map((question, index) => {
                  const sourceItem = liveItems[index];
                  const choices = question.choices?.length ? question.choices : fallbackChoices();
                  return (
                    // biome-ignore lint/a11y/useSemanticElements: the card hosts its own buttons, so it cannot itself be a <button>
                    <div
                      className={`xsq ${reviewed[index] ? '' : 'warn'} ${
                        index === selectedQuestion ? 'cur' : ''
                      }`}
                      id={`q${index}`}
                      key={question.id}
                      role="button"
                      tabIndex={0}
                      {...clickable(() => setSelectedQuestion(index))}
                    >
                      <div className="xsqh">
                        <span className="no">{index + 1}</span>
                        {/* biome-ignore lint/a11y/useSemanticElements: a rich-text stem needs contenteditable, which no HTML control provides */}
                        <div
                          className="xstem"
                          contentEditable
                          role="textbox"
                          suppressContentEditableWarning
                          tabIndex={0}
                        >
                          {question.prompt}
                        </div>
                        {reviewed[index] ? (
                          <span className="ok9">{t('已核准')}</span>
                        ) : (
                          <span className="wn9">{t('待核准')}</span>
                        )}
                        {sourceItem ? (
                          <button
                            aria-label={t('題目選單')}
                            className="ib2"
                            title={t('複製到 Activity')}
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openModal(
                                <CopyActivityItemDialog
                                  contextId={contextId}
                                  sourceActivityId={activity.id}
                                  sourceActivityItemId={sourceItem.id}
                                  sourceItemRevision={sourceItem.revision}
                                />,
                              );
                            }}
                          >
                            ⋮
                          </button>
                        ) : null}
                      </div>
                      <div className="xopts3">
                        {choices.map((choice, position) => (
                          <span
                            className={`xop3 ${choice.text === question.answer ? 'ans' : ''}`}
                            key={choice.id}
                          >
                            {`${choice.label || OPTION_LABELS[position] || ''} ${choice.text}`}
                          </span>
                        ))}
                      </div>
                      <div className="xsqm">
                        <label>
                          {t('答案')}
                          <input
                            defaultValue={question.answer}
                            key={`${activity.id}-${question.id}-a`}
                          />
                          {activity.answerSource === 'ai' && !reviewed[index] ? (
                            <em className="aitag">{t('AI 生成')}</em>
                          ) : null}
                        </label>
                        <label>
                          {t('概念')}
                          <input
                            defaultValue={t(question.concept)}
                            key={`${activity.id}-${question.id}-c`}
                          />
                        </label>
                      </div>
                      {reviewed[index] ? null : (
                        <div className="xsqw">
                          {t('系統對這一題不確定，右邊對一次原卷再確認')}
                          <div className="sp" />
                          <button
                            className="bt sm"
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              actions.reviewQuestion(activity.id, index, 'image');
                            }}
                          >
                            {t('改用原圖')}
                          </button>
                          <button
                            className="bt sm pri"
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              actions.reviewQuestion(activity.id, index, 'confirm');
                            }}
                          >
                            {t('確認無誤')}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {referenceOpen ? <SourcePane /> : null}
      </div>
    </div>
  );
}

function activityQuestionFromEditorItem(item: ActivityEditorItem): ActivityQuestion {
  const choices = item.options.map(
    (option, index): StudentActivityChoice => ({
      id: recordString(option, 'id') ?? `${item.id}-option-${index}`,
      label: recordString(option, 'label') ?? OPTION_LABELS[index] ?? '',
      text: recordString(option, 'text', 'value') ?? '',
    }),
  );
  const selectedIds = Array.isArray(item.answerKey?.selected_option_ids)
    ? item.answerKey.selected_option_ids.filter(
        (value): value is string => typeof value === 'string',
      )
    : [];
  const selectedChoice = choices.find((choice) => selectedIds.includes(choice.id));
  return {
    id: item.id,
    prompt: recordString(item.questionContent, 'text', 'prompt') ?? '',
    answer: selectedChoice?.text ?? recordString(item.answerKey, 'answer', 'correct_answer') ?? '',
    answerMetadata: item.answerKey,
    choices,
    concept: recordString(item.answerKey, 'concept', 'knowledge_component') ?? '',
    reviewed: item.qaApprovedRevision === item.revision,
  };
}

function recordString(
  record: Record<string, unknown> | undefined,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = record?.[key];
    if (typeof value === 'string') return value;
  }
  return undefined;
}

function fallbackChoices(): StudentActivityChoice[] {
  return OPTION_VALUES.map((text, index) => ({
    id: `fallback-${index}`,
    label: OPTION_LABELS[index] ?? '',
    text,
  }));
}

/** The source-scan pane. */
function SourcePane() {
  const t = useTranslator();
  const referenceTab = useActivityUiStore((state) => state.referenceTab);
  const setReferenceTab = useActivityUiStore((state) => state.setReferenceTab);
  const referenceZoom = useActivityUiStore((state) => state.referenceZoom);
  const changeReferenceZoom = useActivityUiStore((state) => state.changeReferenceZoom);
  const setReferenceZoom = useActivityUiStore((state) => state.setReferenceZoom);
  const referencePage = useActivityUiStore((state) => state.referencePage);
  const changeReferencePage = useActivityUiStore((state) => state.changeReferencePage);
  const setReferenceOpen = useActivityUiStore((state) => state.setReferenceOpen);

  return (
    <div className="xedref">
      <PanelResizer max={620} min={300} panel="ref" side="l" />
      <div className="xptool">
        <div className="sp" />
        <button
          className="xzb"
          title={t('縮小')}
          type="button"
          onClick={() => changeReferenceZoom(-10)}
        >
          −
        </button>
        <button
          className="xzv"
          title={t('回到 100%')}
          type="button"
          onClick={() => setReferenceZoom(100)}
        >
          {`${referenceZoom}%`}
        </button>
        <button
          className="xzb"
          title={t('放大')}
          type="button"
          onClick={() => changeReferenceZoom(10)}
        >
          +
        </button>
        <span className="xdiv" />
        <button
          className="xzb"
          title={t('上一頁')}
          type="button"
          onClick={() => changeReferencePage(-1)}
        >
          ‹
        </button>
        <span className="xzp">{`${referencePage} / 9`}</span>
        <button
          className="xzb"
          title={t('下一頁')}
          type="button"
          onClick={() => changeReferencePage(1)}
        >
          ›
        </button>
        <span className="xdiv" />
        <button
          className="xzb"
          title={t('關閉')}
          type="button"
          onClick={() => setReferenceOpen(false)}
        >
          <svg
            aria-hidden="true"
            style={{ width: 13, height: 13, stroke: 'currentColor', fill: 'none', strokeWidth: 2 }}
            viewBox="0 0 24 24"
          >
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>
      <div className="xrftab">
        <button
          className={referenceTab === 'q' ? 'on' : ''}
          type="button"
          onClick={() => setReferenceTab('q')}
        >
          {t('題目原卷')}
        </button>
        <button
          className={referenceTab === 'a' ? 'on' : ''}
          type="button"
          onClick={() => setReferenceTab('a')}
        >
          {t('答案原卷')}
        </button>
      </div>
      <div className="xrfimg">
        <div className="xscan" style={{ zoom: referenceZoom / 100 }}>
          {Array.from({ length: 9 }, (_, index) => (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: decorative scan bars
              key={index}
              style={{ height: `${20 + (((index + referencePage) * 37) % 60)}%` }}
            />
          ))}
          <em>{`${t('原卷第')} ${referencePage} ${t('頁')}`}</em>
        </div>
      </div>
    </div>
  );
}
